/**
 * `data-pl-lightbox` / `data-pl-lightbox-group="GROUP"` — увеличение
 * картинок по клику с overlay'ем.
 *
 * Single-instance overlay переиспользуется для всех групп. Группа определяется
 * значением атрибута `data-pl-lightbox-group` — все `<img>` внутри блоков с
 * одинаковым значением объединяются в одну галерею с навигацией.
 */

import { isEditMode } from '../core/edit-mode.js';
import { t } from '../i18n/index.js';

const INIT_FLAG = 'plLightboxInitialized';
const STYLE_FLAG = 'plLightboxStylesInjected';

const HOVER_STYLES = `
  [data-pl-lightbox] img,
  img[data-pl-lightbox],
  [data-pl-lightbox-group] img,
  img[data-pl-lightbox-group] {
    cursor: pointer;
    transition: transform 0.25s ease, filter 0.25s ease;
    will-change: transform;
  }
  [data-pl-lightbox]:hover img,
  img[data-pl-lightbox]:hover,
  [data-pl-lightbox-group]:hover img,
  img[data-pl-lightbox-group]:hover {
    transform: scale(1.015);
    filter: brightness(1.04);
  }
  html[data-plp-edit] [data-pl-lightbox] img,
  html[data-plp-edit] img[data-pl-lightbox],
  html[data-plp-edit] [data-pl-lightbox-group] img,
  html[data-plp-edit] img[data-pl-lightbox-group] {
    cursor: inherit;
  }
`;

function injectStyles(): void {
  const root = document.documentElement;
  if (root.dataset[STYLE_FLAG]) return;
  root.dataset[STYLE_FLAG] = '1';
  const style = document.createElement('style');
  style.setAttribute('data-pl-lightbox-styles', '');
  style.textContent = HOVER_STYLES;
  document.head.appendChild(style);
}

interface OverlayState {
  el: HTMLElement;
  img: HTMLImageElement;
  prev: HTMLButtonElement;
  next: HTMLButtonElement;
  close: HTMLButtonElement;
  current: number;
  group: HTMLImageElement[];
  trapHandler: (e: KeyboardEvent) => void;
}

let overlay: OverlayState | null = null;

function ensureOverlay(): OverlayState {
  if (overlay) return overlay;
  const el = document.createElement('div');
  el.className = 'plp-lightbox';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  Object.assign(el.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0,0,0,0.9)',
    display: 'none',
    zIndex: '9999',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: '0',
    transition: 'opacity 0.2s ease',
  });

  const img = document.createElement('img');
  Object.assign(img.style, {
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  });

  const prev = makeNavButton('‹', t('lightbox.previous'), 'left');
  const next = makeNavButton('›', t('lightbox.next'), 'right');
  const close = makeNavButton('✕', t('lightbox.close'), 'top-right');

  el.appendChild(img);
  el.appendChild(prev);
  el.appendChild(next);
  el.appendChild(close);
  document.body.appendChild(el);

  const trapHandler = (e: KeyboardEvent): void => {
    if (!overlay || el.style.display === 'none') return;
    if (e.key === 'Escape') closeOverlay();
    else if (e.key === 'ArrowLeft') showAt(overlay.current - 1);
    else if (e.key === 'ArrowRight') showAt(overlay.current + 1);
  };

  el.addEventListener('click', (e) => {
    if (e.target === el) closeOverlay();
  });
  prev.addEventListener('click', (e) => {
    e.stopPropagation();
    if (overlay) showAt(overlay.current - 1);
  });
  next.addEventListener('click', (e) => {
    e.stopPropagation();
    if (overlay) showAt(overlay.current + 1);
  });
  close.addEventListener('click', () => closeOverlay());

  overlay = {
    el,
    img,
    prev,
    next,
    close,
    current: 0,
    group: [],
    trapHandler,
  };
  return overlay;
}

function makeNavButton(
  symbol: string,
  label: string,
  pos: 'left' | 'right' | 'top-right'
): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = symbol;
  b.setAttribute('aria-label', label);
  Object.assign(b.style, {
    position: 'absolute',
    width: '48px',
    height: '48px',
    borderRadius: '9999px',
    border: 'none',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    fontSize: '28px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });
  if (pos === 'left') {
    b.style.left = '20px';
    b.style.top = '50%';
    b.style.transform = 'translateY(-50%)';
  } else if (pos === 'right') {
    b.style.right = '20px';
    b.style.top = '50%';
    b.style.transform = 'translateY(-50%)';
  } else {
    b.style.right = '20px';
    b.style.top = '20px';
  }
  return b;
}

function showAt(index: number): void {
  if (!overlay || overlay.group.length === 0) return;
  const wrapped = ((index % overlay.group.length) + overlay.group.length) % overlay.group.length;
  overlay.current = wrapped;
  overlay.img.src = overlay.group[wrapped].src;
  overlay.img.alt = overlay.group[wrapped].alt ?? '';
  const single = overlay.group.length <= 1;
  overlay.prev.style.display = single ? 'none' : 'flex';
  overlay.next.style.display = single ? 'none' : 'flex';
}

function openOverlay(group: HTMLImageElement[], index: number): void {
  const o = ensureOverlay();
  o.group = group;
  o.el.style.display = 'flex';
  document.addEventListener('keydown', o.trapHandler);
  showAt(index);
  requestAnimationFrame(() => {
    o.el.style.opacity = '1';
  });
  o.close.focus();
}

function closeOverlay(): void {
  if (!overlay) return;
  overlay.el.style.opacity = '0';
  document.removeEventListener('keydown', overlay.trapHandler);
  window.setTimeout(() => {
    if (overlay) overlay.el.style.display = 'none';
  }, 200);
}

function collectGroup(el: HTMLElement): HTMLImageElement[] {
  if (el.matches('[data-pl-lightbox-group]')) {
    return Array.from(el.querySelectorAll('img'));
  }
  const groupName = el.closest('[data-pl-lightbox-group]')?.getAttribute('data-pl-lightbox-group');
  if (groupName) {
    const blocks = document.querySelectorAll(`[data-pl-lightbox-group="${CSS.escape(groupName)}"]`);
    const imgs: HTMLImageElement[] = [];
    blocks.forEach((b) => imgs.push(...Array.from(b.querySelectorAll('img'))));
    return imgs;
  }
  if (el.tagName === 'IMG') return [el as HTMLImageElement];
  const inside = el.querySelector('img');
  return inside ? [inside] : [];
}

function attachLightbox(el: HTMLElement): void {
  const ds = el.dataset;
  if (ds[INIT_FLAG]) return;
  ds[INIT_FLAG] = '1';

  const handler = (e: Event): void => {
    if (isEditMode()) return;
    const target = e.target as HTMLElement;
    const img =
      target.tagName === 'IMG'
        ? (target as HTMLImageElement)
        : el.tagName === 'IMG'
          ? (el as HTMLImageElement)
          : (el.querySelector('img') as HTMLImageElement | null);
    if (!img) return;
    // Если кликнули по <a> внутри галереи — отменяем navigation
    const link = target.closest('a');
    if (link) e.preventDefault();
    e.stopPropagation();
    const group = collectGroup(el);
    const index = Math.max(0, group.indexOf(img));
    openOverlay(group, index);
  };
  el.addEventListener('click', handler);
}

export function initLightbox(nodes: NodeListOf<Element> | Element[]): void {
  const arr = Array.from(nodes);
  if (arr.length > 0) injectStyles();
  for (const node of arr) {
    if (node instanceof HTMLElement) attachLightbox(node);
  }
}
